import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clock, getWavePeaks } from '../../../models/assessmentEvaluation';

// Fat bars with round caps, mirrored about the midline (Assessment Audio).
const BAR = 3;
const GAP = 4;
const HEIGHT = 40;
// Arrow keys step 5 seconds, Page keys 15.
const STEP = 5;
const PAGE = 15;

function buckets(n) {
  const peaks = getWavePeaks();
  const per = peaks.length / n;
  return Array.from({ length: n }, (_, i) => {
    const from = Math.floor(i * per);
    const to = Math.max(from + 1, Math.floor((i + 1) * per));
    const slice = peaks.slice(from, to);
    return slice.length ? slice.reduce((sum, v) => sum + v, 0) / slice.length : 0;
  });
}

/* Everything about the wave except the playhead, worked out once per width:
   the bars and the two colours (the part behind the playhead is ink, the rest
   surface-grey). */
function waveLayout(canvas, width) {
  const n = Math.max(8, Math.floor((width + GAP) / (BAR + GAP)));
  const amps = buckets(n);
  const tokens = getComputedStyle(canvas);
  return {
    width,
    amps,
    max: Math.max(1e-6, ...amps),
    ink: tokens.getPropertyValue('--color-icon-black').trim(),
    rest: tokens.getPropertyValue('--color-surface-grey').trim(),
  };
}

function draw(canvas, layout, played) {
  const ctx = canvas.getContext && canvas.getContext('2d');
  if (!ctx) return;
  const { width, amps, max, ink, rest } = layout;
  const dpr = window.devicePixelRatio || 1;
  if (canvas.width !== Math.floor(width * dpr)) {
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(HEIGHT * dpr);
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, HEIGHT);
  const n = amps.length;
  const mid = HEIGHT / 2;
  const room = mid - BAR / 2; // the round cap needs the rest
  ctx.lineCap = 'round';
  ctx.lineWidth = BAR;
  amps.forEach((amp, i) => {
    const x = BAR / 2 + i * (BAR + GAP);
    const h = (amp / max) * room;
    ctx.strokeStyle = (i + 0.5) / n <= played ? ink : rest;
    ctx.beginPath();
    ctx.moveTo(x, mid - h);
    ctx.lineTo(x, mid + h);
    ctx.stroke();
  });
}

/**
 * The waveform is the scrubber: the design has a play control and the wave
 * and nothing else, so the wave is a slider in its own right and seeks from
 * the keyboard. The canvas is decorative; the slider's value and the time
 * readout carry the same information.
 */
function AssessmentWaveform({ time, duration, onSeek, onToggle }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const stopDrag = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    setWidth(wrap.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => setWidth(wrap.clientWidth));
    observer.observe(wrap);
    return () => observer.disconnect();
  }, []);

  const layout = useMemo(() => (canvasRef.current && width ? waveLayout(canvasRef.current, width) : null), [width]);

  useEffect(() => {
    if (layout) draw(canvasRef.current, layout, duration ? time / duration : 0);
  }, [layout, time, duration]);

  // A drag still running when the dialog closes stops with it.
  useEffect(() => () => stopDrag.current && stopDrag.current(), []);

  const seekAt = useCallback((clientX) => {
    if (!wrapRef.current) return;
    const box = wrapRef.current.getBoundingClientRect();
    if (!box.width) return;
    onSeek(Math.max(0, Math.min(1, (clientX - box.left) / box.width)) * duration);
  }, [duration, onSeek]);

  // Click or drag anywhere on the wave. The drag is followed on the window, so
  // it carries on once the pointer leaves the wave.
  const onPointerDown = (e) => {
    if (e.button !== 0) return; // the primary button only
    seekAt(e.clientX);
    wrapRef.current.focus();
    const move = (ev) => seekAt(ev.clientX);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      stopDrag.current = null;
    };
    stopDrag.current = up;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const onKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowRight': case 'ArrowUp': onSeek(time + STEP); break;
      case 'ArrowLeft': case 'ArrowDown': onSeek(time - STEP); break;
      case 'PageUp': onSeek(time + PAGE); break;
      case 'PageDown': onSeek(time - PAGE); break;
      case 'Home': onSeek(0); break;
      case 'End': onSeek(duration); break;
      case ' ': case 'Enter': onToggle(); break;
      default: return;
    }
    e.preventDefault();
  };

  return (
    <div
      ref={wrapRef}
      className="assessment-wave"
      role="slider"
      tabIndex={0}
      aria-label="Seek"
      aria-valuemin={0}
      aria-valuemax={Math.round(duration)}
      aria-valuenow={Math.round(time)}
      aria-valuetext={clock(time)}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    >
      <canvas ref={canvasRef} aria-hidden="true" />
    </div>
  );
}

export default AssessmentWaveform;
